import BarChartIcon from '@mui/icons-material/BarChart'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import { Box, Collapse, List, ListItemButton, ListItemIcon, ListItemText, Tooltip } from '@mui/material'
import { MouseEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router-dom'

import { favoriteReportsService } from '../api/services/reports.service'
import PaginatedReportIcon from '../assets/images/PaginatedReportIcon.svg?react'
import { useCurrentSite } from '../store/store.hooks'
import { Report } from '../types'
import { logger } from '../utils/logger'
import { localizeReportName } from './localizeReportName'
import { useFavoriteReportIds, useReportList } from './services/ReportsHooks'


const FAVORITE_STAR_COLOR = '#FDD106'

const FavoriteStar = ({ isFavorite, onToggle }: { isFavorite: boolean; onToggle: () => void }) => {
  const Icon = isFavorite ? StarIcon : StarBorderIcon

  return (
    <Icon
      fontSize="small"
      onClick={(e: MouseEvent) => {
        e.preventDefault()
        e.stopPropagation()
        onToggle()
      }}
      sx={{
        color: isFavorite ? FAVORITE_STAR_COLOR : 'inherit',
        '&:hover': { color: FAVORITE_STAR_COLOR },
      }}
    />
  )
}

const ReportsContainer = ({ isOpen = true, onOpen }: { isOpen?: boolean; onOpen?: () => void }) => {
  const { t, i18n } = useTranslation()
  const location = useLocation()
  const currentSite = useCurrentSite()
  const isProductViewActivated = currentSite?.isProductViewActivated ?? true

  const { reports } = useReportList()

  const favoriteReportIdsFromServer = useFavoriteReportIds()

  const siteId = currentSite?.id

  useEffect(() => {
    logger.log('[ReportsContainer] Reports:', reports)
  }, [currentSite?.id, reports])

  useEffect(() => {
    logger.log('[ReportsContainer] Favorite report ids:', favoriteReportIdsFromServer)
  }, [currentSite?.id, favoriteReportIdsFromServer])

  const [openFavorites, setOpenFavorites] = useState(true)
  const [openPowerBI, setOpenPowerBI] = useState(false)
  const [openPaginated, setOpenPaginated] = useState(false)
  const [favoriteReportIds, setFavoriteReportIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    setFavoriteReportIds(new Set(favoriteReportIdsFromServer))
  }, [favoriteReportIdsFromServer])

  const handleAddFavorite = (reportId: string) => {
    if (!siteId) return

    setFavoriteReportIds((prev) => new Set(prev).add(reportId))

    favoriteReportsService.add(siteId, reportId).catch(() => {})
    logger.log(`[ReportsContainer] Added report ${reportId} to favorites`)
  }

  const handleRemoveFavorite = (reportId: string) => {
    if (!siteId) return

    setFavoriteReportIds((prev) => {
      const next = new Set(prev)
      next.delete(reportId)
      return next
    })

    favoriteReportsService.remove(siteId, reportId).catch(() => {})
    logger.log(`[ReportsContainer] Removed report ${reportId} from favorites`)
  }

  const favoriteReports = reports.filter((report) => favoriteReportIds.has(report.id))
  const paginatedReports = reports.filter(
    (report) => report.reportType === 'PaginatedReport' && !favoriteReportIds.has(report.id),
  )
  const powerBIReports = reports.filter(
    (report) => report.reportType !== 'PaginatedReport' && !favoriteReportIds.has(report.id),
  )

  const renderReportItem = (report: Report) => {
    const isPaginated = report.reportType === 'PaginatedReport'
    const isFavorite = favoriteReportIds.has(report.id)
    const reportName = localizeReportName(i18n, report.name)

    return (
      <ListItemButton
        key={report.id}
        selected={location.pathname.includes(report.id)}
        component={Link}
        to={`/reports/${report.workspaceId}/${report.id}`}
        sx={{ pl: 4 }}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>
          {isPaginated ? <PaginatedReportIcon /> : <BarChartIcon htmlColor="black" />}
        </ListItemIcon>
        <Tooltip title={reportName} placement="right" enterDelay={500}>
          <ListItemText primary={reportName} primaryTypographyProps={{ noWrap: true }} />
        </Tooltip>
        <FavoriteStar
          isFavorite={isFavorite}
          onToggle={() => (isFavorite ? handleRemoveFavorite(report.id) : handleAddFavorite(report.id))}
        />
      </ListItemButton>
    )
  }

  return (
    <Box>
      <List>
        {favoriteReports.length > 0 && (
          <>
            <Tooltip title={!isOpen ? t('reports.favoritesTitle') : ''} placement="right">
              <ListItemButton
                onClick={() => (isOpen ? setOpenFavorites((prev) => !prev) : onOpen?.())}
                selected={favoriteReports.some((r) => location.pathname.includes(r.id))}
              >
                <ListItemIcon>
                  <StarIcon sx={{ color: FAVORITE_STAR_COLOR }} />
                </ListItemIcon>
                {isOpen && <ListItemText primary={t('reports.favoritesTitle')} />}
                {isOpen && (openFavorites ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
              </ListItemButton>
            </Tooltip>
            <Collapse in={isOpen && openFavorites} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {favoriteReports.map(renderReportItem)}
              </List>
            </Collapse>
          </>
        )}

        {powerBIReports.length > 0 && (
          <>
            {isProductViewActivated ? (
              <>
                <Tooltip title={!isOpen ? t('reports.analyticsTitle') : ''} placement="right">
                  <ListItemButton
                    onClick={() => (isOpen ? setOpenPowerBI((prev) => !prev) : onOpen?.())}
                    selected={powerBIReports.some((r) => location.pathname.includes(r.id))}
                  >
                    <ListItemIcon>
                      <BarChartIcon htmlColor="black" />
                    </ListItemIcon>
                    {isOpen && <ListItemText primary={t('reports.analyticsTitle')} />}
                    {isOpen && (openPowerBI ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
                  </ListItemButton>
                </Tooltip>
                <Collapse in={isOpen && openPowerBI} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding>
                    {powerBIReports.map(renderReportItem)}
                  </List>
                </Collapse>
              </>
            ) : (
              <>
                <ListItemButton disabled sx={{ opacity: '1 !important' }}>
                  <ListItemIcon>
                    <BarChartIcon htmlColor="black" />
                  </ListItemIcon>
                  <ListItemText primary={t('reports.analyticsTitle')} />
                </ListItemButton>
                <List component="div" disablePadding>
                  {powerBIReports.map(renderReportItem)}
                </List>
              </>
            )}
          </>
        )}

        {paginatedReports.length > 0 && (
          <>
            <Tooltip title={!isOpen ? t('reports.reportListTitle') : ''} placement="right">
              <ListItemButton
                onClick={() => (isOpen ? setOpenPaginated((prev) => !prev) : onOpen?.())}
                selected={paginatedReports.some((r) => location.pathname.includes(r.id))}
              >
                <ListItemIcon>
                  <PaginatedReportIcon style={{ color: 'black' }} />
                </ListItemIcon>
                {isOpen && <ListItemText primary={t('reports.reportListTitle')} />}
                {isOpen && (openPaginated ? <ExpandLessIcon /> : <ExpandMoreIcon />)}
              </ListItemButton>
            </Tooltip>
            <Collapse in={isOpen && openPaginated} timeout="auto" unmountOnExit>
              <List component="div" disablePadding>
                {paginatedReports.map(renderReportItem)}
              </List>
            </Collapse>
          </>
        )}
      </List>
    </Box>
  )
}

export default ReportsContainer
